/**
 * @module @yimoka/i18n/typed-t
 * @summary 从 bundled 推导 t() 键类型
 * @author keep
 * @since 2026-05-19
 */

import type { BundledMap } from './types';

/** 递归展开 object 键为 dot path */
type FlattenKeys<T, Prefix extends string = ''> = T extends Record<string, unknown>
  ? {
      [K in keyof T & string]: T[K] extends Record<string, unknown>
        ? FlattenKeys<T[K], Prefix extends '' ? K : `${Prefix}.${K}`>
        : Prefix extends '' ? K : `${Prefix}.${K}`;
    }[keyof T & string]
  : never;

/** 从 defaultLng bundled 推导 namespace:key */
type NamespaceKeys<TBundled extends BundledMap, TLng extends keyof TBundled & string> = {
  [NS in keyof TBundled[TLng] & string]: `${NS}:${FlattenKeys<TBundled[TLng][NS]>}`;
}[keyof TBundled[TLng] & string];

/** TypedT 键联合 */
export type TypedTKey<TBundled extends BundledMap, TDefaultLng extends keyof TBundled & string> =
  NamespaceKeys<TBundled, TDefaultLng>;

/**
 * 绑定 i18next t 并保留类型。
 *
 * @param tFn - i18next t
 * @returns 带类型的 t
 */
export function createTypedT<TBundled extends BundledMap, TDefaultLng extends keyof TBundled & string>(
  tFn: (key: string, options?: Record<string, unknown>) => string,
): (key: TypedTKey<TBundled, TDefaultLng> | string, options?: Record<string, unknown>) => string {
  return tFn as (key: TypedTKey<TBundled, TDefaultLng> | string, options?: Record<string, unknown>) => string;
}
